import PropTypes from 'prop-types';
import cx from 'classnames';

import withTopic from 'core/hocs/withTopic';
import withTheme from 'core/hocs/withTheme';
import Expert from 'core/components/Expert';
import MarkdownWrapper from 'core/components/MarkdownWrapper';

import { compose } from 'core/libs/recompose';
import { withRouter } from 'core/libs/router';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { getPage } from 'core/utils/url-helper';
import resolveRelationships from 'core/utils/relationships';
import themePropTypes from 'core/utils/prop-types/theme';
import { isSetButFalsy } from 'core/utils/common';

import styles from './index.styl';


/**
 * Компонент заголовка, подзаголовка и лида для топиков
 * @author Igor Tomkovich <i.tomkovich@rambler-co.ru>
 */

const relationships = resolveRelationships(
  ['content', 'authors'],
  {},
  { content: { widgets: [], authors: [] } }
);

const getTextsFromPageBreak = (page, widgets) => {
  const pageBreaks = widgets.filter(widget => widget.type === 'pageBreak');
  const {
    title: titleFromPageBreak,
    caption: leadFromPageBreak,
  } = page > 1 && ((pageBreaks[page - 2]).attributes || {});
  return { titleFromPageBreak, leadFromPageBreak };
};

const TopicHeaderTexts = (props) => {
  const {
    scopedStyles,
    title: titleFromProps,
    subtitle: subtitleFromProps,
    lead: leadFromProps,
    capitalizeFirstLetter,
    isPremiumTopic,
    topic: {
      infinityIndex,
      content,
      content: {
        attributes: {
          headline: titleFromTopic,
          alternative_headline: subtitleFromTopic,
          announce: leadFromTopic,
        },
      },
    },
    theme: {
      controls: {
        topicHeaderTexts: atoms,
        topicHeaderTexts: {
          premiumTopic: premiumTopicAtoms,
        },
      },
    },
    location: {
      search,
    },
  } = props;

  const {
    authors,
    content: {
      widgets,
    },
  } = relationships(content);

  const page = infinityIndex > 0
    ? 1
    : getPage(search);

  // выключаем сабтайтл или лид если напрямую передано какое-то falsy value
  const isSubtitleDisabled = isSetButFalsy(subtitleFromProps);
  const isLeadDisabled = isSetButFalsy(leadFromProps);

  let title = titleFromProps || titleFromTopic;
  // для первых страниц берем сабтайтл и лид из пропсов, или если не задан то из топика
  let subtitle = !isSubtitleDisabled && (subtitleFromProps || subtitleFromTopic);
  let lead = !isLeadDisabled && (leadFromProps || leadFromTopic);
  const expert = authors.find(author => author.attributes.type === 'expert');
  if (page > 1) {
    const {
      titleFromPageBreak,
      leadFromPageBreak,
    } = getTextsFromPageBreak(page, widgets);
    // для НЕпервых страниц берем тайтл из pageBreak если он задан, если нет, то общий
    title = titleFromPageBreak || title;
    // для НЕпервых страниц сабтайтл не показываем
    subtitle = false;
    // для НЕпервых страниц берем лид из pageBreak, или если не задан то не показываем
    lead = leadFromPageBreak || false;
  }

  const dividerMargin = atoms.divider?.margin;
  const dividerBg = atoms.divider?.background;

  const dividerScopeStyles = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .divider
          &::before
            display block
            content ''
            height 1px
            margin ${dividerMargin}
            background ${dividerBg}
      `}</style>
    </scope>
  );

  const capitalizeScopeStyles = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .${styles._capitalizeFirstLetter}
          &:first-letter
            font ${atoms.firstLetter.font}
      `}</style>
    </scope>
  );

  return (
    <div className={cx(styles.content, isPremiumTopic && styles._premium)}>
      <style jsx>{`
        .${styles.content}
          margin-bottom ${atoms.marginBottom}px
          &.${styles._premium}
            :global(.desktop) &
              margin-bottom 0

        .${styles.title}
          font ${atoms.title.font}
          color ${atoms.title.color}
          letter-spacing ${atoms.title.letterSpacing || 0}
          .${styles._premium} &
            font ${premiumTopicAtoms.title.font}

        .${styles.subtitle}
          font ${atoms.subtitle.font}
          color ${atoms.subtitle.color}
          letter-spacing ${atoms.subtitle.letterSpacing || 0}
          .${styles._premium} &
            color ${premiumTopicAtoms.textColor}
            font ${premiumTopicAtoms.subtitle.font}

        .lead
          font ${atoms.lead.font}
          color ${atoms.lead.color}
          letter-spacing ${atoms.lead.letterSpacing || 0}
          margin-top ${atoms.lead.top}px

        .${styles.expert}
          margin ${atoms.expert.margin}
      `}</style>
      <h1
        className={cx(
          styles.title,
          scopedStyles && scopedStyles.wrapClassNames('title'),
          capitalizeFirstLetter && capitalizeScopeStyles.wrapClassNames(styles._capitalizeFirstLetter),
        )}
        data-qa='lb-topic-header-texts-title'
      >
        <MarkdownWrapper inline>
          {title}
        </MarkdownWrapper>
      </h1>
      {' '}
      {subtitle && (
        <span
          data-qa='lb-topic-header-texts-subtitle'
          className={cx(
            styles.subtitle,
            scopedStyles && scopedStyles.wrapClassNames('subtitle')
          )}
        >
          <MarkdownWrapper inline>
            {subtitle}
          </MarkdownWrapper>
        </span>
      )}
      {expert && (
        <div className={styles.expert}>
          <Expert content={expert} />
        </div>
      )}
      {(lead && !isPremiumTopic) && (
        <div
          className={cx(
            'lead',
            scopedStyles && scopedStyles.wrapClassNames('lead'),
            atoms.divider && dividerScopeStyles.wrapClassNames('divider'),
          )}
          data-qa='lb-topic-header-texts-lead'
        >
          <MarkdownWrapper inline>
            {lead}
          </MarkdownWrapper>
        </div>
      )}
      {scopedStyles && <scopedStyles.styles />}
      {atoms.divider && <dividerScopeStyles.styles />}
      {capitalizeFirstLetter && <capitalizeScopeStyles.styles />}
    </div>
  );
};

const styleContract = `
  {
    font,
    color,
  }
`;

TopicHeaderTexts.propTypes = {
  isPremiumTopic: PropTypes.bool,
  /** Объект resolveScopedStyles для стилизации */
  scopedStyles: PropTypes.object,
  /** Заголовок */
  title: PropTypes.string,
  /** Подзаголовок */
  subtitle: PropTypes.string,
  /** Лид */
  lead: PropTypes.string,
  /** Начинать ли заголовок с прописной буквы */
  capitalizeFirstLetter: PropTypes.bool,
  /** Данные топика */
  topic: PropTypes.object,
  /** @ignore */
  location: PropTypes.object.isRequired,
  /** @ignore */
  theme: themePropTypes(`
    {
      controls: {
        topicHeaderTexts: {
          title: ${styleContract},
          subtitle: ${styleContract},
          lead: {
            ${styleContract},
            top,
          },
        },
      },
    }
  `),
};

const TopicHeaderTextsWithHOCs = compose(
  withTheme,
  withTopic,
  withRouter,
)(TopicHeaderTexts);

TopicHeaderTextsWithHOCs.displayName = 'TopicHeaderTexts';

export { TopicHeaderTexts as StorybookComponent };
export default TopicHeaderTextsWithHOCs;
